import type { Dictionary } from './dictionary.no-deps'

/**
 * Partially applied `map` for Map data structure to be used in `pipe` e.g.
 *
 *    pipe(
 *        mapFromDict({ foo: 0, bar: 1, quiz: 2 }),
 *        mapMap((_, n) => n + 1)        // mapFromDict({ foo: 1, bar: 2, quiz: 3 })
 *    )
 */
export function mapMap<K, V1, V2>(fn: (k: K, a: V1) => V2) {
    return (m1: Map<K, V1>): Map<K, V2> => {
        const m2 = new Map<K, V2>()

        for (const [key, value] of m1) {
            m2.set(key, fn(key, value))
        }

        return m2
    }
}

/**
 * Partially applied `filter` for Map data structure to be used in `pipe` e.g.
 *
 *    pipe(
 *        mapFromDict({ foo: 0, bar: 1, quiz: 2 }),
 *        mapFilter((_, v) => isEven(v))        // mapFromDict({ foo: 0, quiz: 2 })
 *    )
 */
export function mapFilter<K, V>(predicate: (k: K, v: V) => boolean) {
    return (m1: Map<K, V>): Map<K, V> => {
        const m2 = new Map<K, V>()

        for (const [key, value] of m1) {
            if (!predicate(key, value)) continue
            m2.set(key, value)
        }

        return m2
    }
}

export function mapMerge<K, V>(a: Map<K, V>, b: Map<K, V>): Map<K, V> {
    return new Map([...a, ...b])
}

export function mapKeys<K, V>(m: Map<K, V>): K[] {
    return [...m.keys()]
}

export function mapValues<K, V>(m: Map<K, V>): V[] {
    return [...m.values()]
}

export function mapSet<K, V>(m: Map<K, V>, key: K, value: V): Map<K, V> {
    return mapUpdate(m, key, () => value)
}

export function mapUpdate<K, V>(m1: Map<K, V>, key: K, fn: (v: V) => V): Map<K, V> {
    const m2 = new Map(m1)
    m2.set(key, fn(m2.get(key)))
    return m2
}

export function mapRemove<K, V>(m1: Map<K, V>, key: K): Map<K, V> {
    const m2 = new Map(m1)
    m2.delete(key)
    return m2
}

export function mapFromArray<K, V>(array: V[], getKey: (t: V) => K) {
    const m = new Map<K, V>()

    for (const value of array) {
        m.set(getKey(value), value)
    }

    return m
}

export function mapFromDict<V>(dict: Dictionary<V>): Map<string, V> {
    return new Map(Object.entries(dict))
}
